/**
 * Navbar Component
 * Responsive navigation with mobile hamburger menu
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ContactModal from '../common/ContactModal';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Courses', path: '/courses' },
    { name: 'Jobs', path: '/jobs' },
    { name: 'Contact Us', path: '/contact', isContact: true },
  ];

  const isActive = (path) => location.pathname === path;

  const handleContactClick = (e) => {
    e.preventDefault();
    setShowContactModal(true);
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getDashboardPath = () => {
    if (!user) return '/login';
    return `/${user.role}/dashboard`;
  };

  return (
    <nav className="lh-navbar fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
      <div className="lh-navbar__container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lh-navbar__content flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="lh-navbar__logo flex items-center gap-2">
            <img src="/lat-icon.png" alt="Lotus Arc Technologies" className="h-10 w-auto" />
            <span className="text-base sm:text-xl font-bold text-dark-900 whitespace-nowrap">Lotus Arc Technologies</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="lh-navbar__links hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isContact ? (
                <a
                  key={link.path}
                  href="#footer"
                  onClick={handleContactClick}
                  className={`lh-navbar__link lh-navbar__link--${link.name.toLowerCase().replace(' ', '-')} ${isActive(link.path) ? 'lh-navbar__link--active text-primary-500' : 'text-dark-600 hover:text-primary-500'} font-medium transition-colors cursor-pointer`}
                >
                  {link.name}
                </a>
              ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`lh-navbar__link lh-navbar__link--${link.name.toLowerCase().replace(' ', '-')} ${isActive(link.path) ? 'lh-navbar__link--active text-primary-500' : 'text-dark-600 hover:text-primary-500'} font-medium transition-colors`}
              >
                {link.name}
              </Link>
              )
            ))}
          </div>

          {/* Auth Buttons - Show when logged out */}
          {!user && (
          <div className="lh-navbar__auth hidden md:flex items-center gap-3">
            <Link
              to="/login"
              state={{ from: location.pathname }}
              className="lh-navbar__auth-login px-4 py-2 text-dark-700 hover:text-primary-500 font-medium transition-colors"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="lh-navbar__auth-signup px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all hover:shadow-lg"
            >
              Sign Up
            </Link>
          </div>
          )}

          {/* User Menu - Show when logged in */}
          {user && (
            <div className="lh-navbar__user hidden md:flex items-center gap-4">
              <div className="lh-navbar__user-menu relative group">
                <button className="lh-navbar__user-button flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-dark-50 transition-colors">
                  <div className="lh-navbar__user-avatar w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span>{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                    )}
                  </div>
                  <span className="lh-navbar__user-name text-dark-700 font-medium text-sm truncate max-w-[120px]">{user.name || 'User'}</span>
                  <svg className="lh-navbar__user-chevron w-4 h-4 text-dark-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="lh-navbar__user-dropdown absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-dark-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="lh-navbar__user-info px-4 py-3 border-b border-dark-100">
                    <p className="lh-navbar__user-email text-sm text-dark-600 truncate" title={user.email}>{user.email}</p>
                    <p className="lh-navbar__user-role text-xs text-primary-500 capitalize mt-1">{user.role}</p>
                  </div>
                  <div className="lh-navbar__user-actions py-2">
                    <Link
                      to={getDashboardPath()}
                      className="lh-navbar__user-link block px-4 py-2 text-sm text-dark-700 hover:bg-dark-50 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Dashboard
                    </Link>
                    <Link
                      to="/profile"
                      className="lh-navbar__user-link block px-4 py-2 text-sm text-dark-700 hover:bg-dark-50 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="lh-navbar__user-logout w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="lh-navbar__mobile-btn md:hidden p-2 text-dark-600 hover:text-primary-500"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <svg className="lh-navbar__mobile-icon lh-navbar__mobile-icon--close w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="lh-navbar__mobile-icon lh-navbar__mobile-icon--open w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lh-navbar__mobile-menu md:hidden py-4 border-t border-dark-100 animate-fade-in">
            <div className="lh-navbar__mobile-links flex flex-col gap-4">
              {navLinks.map((link) => (
                link.isContact ? (
                  <a
                    key={link.path}
                    href="#footer"
                    onClick={handleContactClick}
                    className={`lh-navbar__mobile-link lh-navbar__mobile-link--${link.name.toLowerCase().replace(' ', '-')} ${isActive(link.path) ? 'text-primary-500' : 'text-dark-600'} font-medium py-2 cursor-pointer`}
                  >
                    {link.name}
                  </a>
                ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`lh-navbar__mobile-link lh-navbar__mobile-link--${link.name.toLowerCase().replace(' ', '-')} ${isActive(link.path) ? 'text-primary-500' : 'text-dark-600'} font-medium py-2`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
                )
              ))}
              <hr className="lh-navbar__mobile-divider border-dark-100" />
              
              {/* Mobile Auth - Show when logged out */}
              {!user && (
                <>
              <Link
                to="/login"
                state={{ from: location.pathname }}
                className="lh-navbar__mobile-login text-dark-700 font-medium py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="lh-navbar__mobile-signup bg-primary-500 text-white font-semibold py-3 px-4 rounded-lg text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>
                </>
              )}

              {/* Mobile User Menu - Show when logged in */}
              {user && (
                <>
                  <div className="lh-navbar__mobile-user-info px-4 py-3 border-t border-dark-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="lh-navbar__user-avatar w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span>{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="lh-navbar__user-name text-dark-900 font-medium truncate">{user.name || 'User'}</p>
                        <p className="lh-navbar__user-email text-sm text-dark-500 truncate" title={user.email}>{user.email}</p>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={getDashboardPath()}
                    className="lh-navbar__mobile-link text-dark-600 font-medium py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    className="lh-navbar__mobile-link text-dark-600 font-medium py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="lh-navbar__mobile-logout text-red-600 font-medium py-2 text-left"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <ContactModal open={showContactModal} onClose={() => setShowContactModal(false)} />
    </nav>
  );
};

export default Navbar;
